import { useEffect, useState } from 'react';
import { Download } from 'lucide-react';

/**
 * `beforeinstallprompt` is a non-standard Chromium event. Typing it
 * ourselves so we don't pull in `@types/wicg-file-system-access` or
 * similar polyfills just for one event.
 */
interface BeforeInstallPromptEvent extends Event {
    readonly platforms: string[];
    prompt(): Promise<void>;
    readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

const DISMISSED_KEY = 'pwa:install-dismissed-at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

function recentlyDismissed(): boolean {
    try {
        const raw = localStorage.getItem(DISMISSED_KEY);
        if (!raw) return false;
        const ts = Number(raw);
        if (!Number.isFinite(ts)) return false;
        return Date.now() - ts < DISMISS_TTL_MS;
    } catch {
        return false;
    }
}

/**
 * Floating "Install app" button. Visible only when:
 *   - the browser fired `beforeinstallprompt` (i.e. the PWA is installable)
 *   - the user hasn't dismissed the prompt within DISMISS_TTL_MS
 *   - the app isn't already running as an installed PWA
 */
export function InstallPrompt() {
    const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);

    useEffect(() => {
        if (window.matchMedia('(display-mode: standalone)').matches) return;
        if (recentlyDismissed()) return;

        const handler = (event: Event) => {
            event.preventDefault();
            setDeferred(event as BeforeInstallPromptEvent);
        };
        window.addEventListener('beforeinstallprompt', handler);
        return () => window.removeEventListener('beforeinstallprompt', handler);
    }, []);

    if (!deferred) return null;

    const handleInstall = async () => {
        await deferred.prompt();
        await deferred.userChoice;
        setDeferred(null);
    };

    const handleDismiss = () => {
        try {
            localStorage.setItem(DISMISSED_KEY, String(Date.now()));
        } catch {
            /* storage may be disabled — fall through */
        }
        setDeferred(null);
    };

    return (
        <div className="fixed bottom-4 left-4 z-[9998] bg-white shadow-lg rounded-xl border border-gray-200 p-3 flex items-center gap-3 max-w-xs">
            <div className="bg-blue-50 rounded-lg p-2">
                <Download className="w-5 h-5 text-blue-600" aria-hidden />
            </div>
            <div className="flex-1 text-sm">
                <div className="font-medium text-gray-900">Install app</div>
                <div className="text-gray-500 text-xs">Use offline, launch from home screen.</div>
            </div>
            <div className="flex flex-col gap-1">
                <button
                    onClick={handleInstall}
                    className="text-xs font-semibold text-blue-600 hover:text-blue-700 px-2 py-1 rounded"
                >
                    Install
                </button>
                <button
                    onClick={handleDismiss}
                    className="text-xs text-gray-400 hover:text-gray-600 px-2 py-1 rounded"
                >
                    Later
                </button>
            </div>
        </div>
    );
}
